import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { ChainConfigContext, IChain } from './ChainConfigContext';
import { ChainContext } from './ChainContext';
import useContract from '../hooks/useContract';
import { FormContext } from './FormContext';
import ERC20_ABI from "../config/abis/ERC20.json";
import { isNativeToken, sortTokenList } from '../utils/tokenUtils';
import BLOCKED_TOKENS from '../config/blockedTokens';
import useCoingecko from '../hooks/useCoingecko';
import { getProvider } from '../utils/getProvider';

const MULTICALL_ABI = [
  'function aggregate(tuple(address target, bytes callData)[] calls) view returns (uint256 blockNumber, bytes[] returnData)',
  'function getEthBalance(address addr) view returns (uint256 balance)',
  'function tryAggregate(bool requireSuccess, tuple(address target, bytes callData)[] calls) view returns (tuple(bool success, bytes returnData)[] returnData)'
];

interface TokenBalancesContextType {
  getBalance: (chainId: number, tokenAddress: string) => string;
  getLoading: (chainId: number, tokenAddress?: string) => boolean;
  refreshBalances: () => Promise<void>;
  getPrice: (chainId: number, tokenAddress: string) => number | null;
}

const TokenBalancesContext = createContext<TokenBalancesContextType>({
  getBalance: (chainId: number, tokenAddress: string) => '0',
  getLoading: (chainId: number) => false,
  refreshBalances: () => Promise.resolve(),
  getPrice: (chainId: number, tokenAddress: string) => null,
});

const DEFAULT_CHUNK_SIZE = 100; 

const TokenBalancesProvider = ({ children }) => {

  const { form } = useContext(FormContext);
  const { supportedTokens, getChainById } = useContext(ChainConfigContext);
  const { connectedAccount, selectedChainId } = useContext(ChainContext);
  const { getPricesByTokenAddresses } = useCoingecko();
  const [balances, setBalances] = useState<{ [chainId: number]: { [tokenAddress: string]: string } }>({});
  const [prices, setPrices] = useState<{ [chainId: number]: { [tokenAddress: string]: number } }>({});
  const [loading, setLoading] = useState<{ [chainId: number]: boolean }>({});

  const chainFrom: IChain | undefined = getChainById(form.chainIdFrom);
  const chainTo: IChain | undefined = getChainById(form.chainIdTo);

  const fetchBalancesForChain = useCallback(async (chainId: number) => {
    
    const provider = getProvider(chainId);
    const chain: IChain | undefined = getChainById(chainId);

    if (!connectedAccount || !chain) return;

    const multicall = new ethers.Contract(
      chain?.useDappChain?.multicall2Address || chain?.useDappChain?.multicallAddress || '', 
      MULTICALL_ABI, 
      provider
    );

    const multicallAddress = await multicall.getAddress();
    const tokens = sortTokenList(supportedTokens?.[chainId]?.filter(token => !(BLOCKED_TOKENS?.[chainId]?.map(t => t.toLowerCase()) || [])?.includes(token.address.toLowerCase())) || []);
    if (tokens.length === 0) return;

    setLoading(prev => ({ ...prev, [chainId]: true }));

    const erc20Interface = new ethers.Interface(ERC20_ABI); 
    const multicallInterface = new ethers.Interface(MULTICALL_ABI); 

    // Initialize balances for this chain to empty object to clear previous data
    setBalances(prev => ({ ...prev, [chainId]: {} }));

    // Keep track of all balances for price fetching
    const allBalances: { [tokenAddress: string]: string } = {};

    for (let i = 0; i < tokens.length; i += chain?.maxChunkSize || DEFAULT_CHUNK_SIZE) {
      const chunk = tokens.slice(i, i + (chain?.maxChunkSize || DEFAULT_CHUNK_SIZE));
      const calls = chunk.map(token => ({
        target: isNativeToken(token.address) 
          ? multicallAddress
          : token.address,
        callData: isNativeToken(token.address) 
          ? multicallInterface.encodeFunctionData('getEthBalance', [connectedAccount])
          : erc20Interface.encodeFunctionData('balanceOf', [connectedAccount])
      }));

      try {
        const response = await (chain?.useDappChain?.multicall2Address 
          ? multicall.tryAggregate(false, calls) 
          : multicall.aggregate(calls)
        );

        let processedReturnData;
        if (chain?.useDappChain?.multicall2Address) {
          processedReturnData = response.map(item => item.returnData);
        } else {
          processedReturnData = response.returnData;
        }
        
        const chunkBalances: { [tokenAddress: string]: string } = {};
        chunk.forEach((token, index) => {
          let balance = processedReturnData?.[index];
          if (!balance || balance === '0x' || balance === '0x0') {
            balance = '0x0';
          }
          const formattedBalance = ethers.formatUnits(balance, token.decimals);
          chunkBalances[token.address] = formattedBalance;
          allBalances[token.address] = formattedBalance;
        });

        setBalances(prev => ({ 
          ...prev, 
          [chainId]: { 
            ...prev[chainId], 
            ...chunkBalances 
          } 
        }));
      } catch (error) {
        console.error(`Error fetching balances for chain ${chainId}`, error);
      }
    }

    setLoading(prev => ({ ...prev, [chainId]: false }));

    try {
      // Use the collected balances for price fetching
      const tokensWithBalance = tokens.filter(token => 
        parseFloat(allBalances[token.address] || '0') > 0
      );
      const tokenAddresses = tokensWithBalance.map(token => isNativeToken(token.address) ? (chain?.weth || token.address) : token.address);
      if (tokenAddresses.length > 0) {
        const pricesData = await getPricesByTokenAddresses(chainId, tokenAddresses);
        if (pricesData) {
          const newPrices: { [tokenAddress: string]: number } = {};
          pricesData.forEach(({ address, price }) => {
            newPrices[address] = price;
          });
          setPrices(prev => ({ ...prev, [chainId]: newPrices }));
        }
      }
    } catch (error) {
      console.error(`Error fetching prices for chain ${chainId}`, error);
    }
  }, [connectedAccount, supportedTokens, chainFrom?.chainId, chainTo?.chainId]);

  const refreshBalances = useCallback(async () => {
    const fetchPromises: Promise<void>[] = [];
    if (chainFrom) {
      setLoading(prev => ({ ...prev, [chainFrom?.chainId]: true }));
      fetchPromises.push(fetchBalancesForChain(chainFrom.chainId));
    }
    if (chainTo && chainTo.chainId !== chainFrom?.chainId) {
      setLoading(prev => ({ ...prev, [chainTo?.chainId]: true }));
      fetchPromises.push(fetchBalancesForChain(chainTo.chainId));
    }
    try {
      await Promise.allSettled(fetchPromises);
    } catch (error) {
      console.error('Error refreshing balances', error);
    } finally {
      setLoading(prev => ({ ...prev, 
        [chainFrom?.chainId ?? '']: false, 
        [chainTo?.chainId ?? '']: false 
      }));
    }
  }, [chainFrom, chainTo, fetchBalancesForChain]);

  useEffect(() => {
    setBalances({});
    setLoading({});
    if (selectedChainId !== chainFrom?.chainId) return;
    if (!connectedAccount) return;
    refreshBalances();
  }, [
    connectedAccount,
    selectedChainId,
    chainFrom?.chainId,
    chainTo?.chainId,
    supportedTokens,
    refreshBalances,
  ]);

  const getBalance = useCallback((chainId: number, tokenAddress: string): string => {
    return connectedAccount ? (balances?.[chainId]?.[tokenAddress] || '0') : '0';
  }, [balances, connectedAccount]);

  const getPrice = useCallback((chainId: number, tokenAddress: string): number | null => {
    const chain = chainFrom?.chainId === chainId ? chainFrom : chainTo;
    if (isNativeToken(tokenAddress)) tokenAddress = chain?.weth || tokenAddress;
    return prices?.[chainId]?.[tokenAddress] ?? null;
  }, [prices]);

  const getLoading = useCallback((chainId: number, tokenAddress?: string): boolean => {
    if (!connectedAccount) {
      return false;
    }
    if (!tokenAddress) {
      return loading?.[chainId] || false;
    }
    if (balances?.[chainId]?.[tokenAddress] !== undefined) {
      return false;
    }
    return loading?.[chainId] || false;
  }, [loading, balances, connectedAccount]);

  return (
    <TokenBalancesContext.Provider value={{
      getBalance,
      getLoading,
      refreshBalances,
      getPrice
    }}>
      {children}
    </TokenBalancesContext.Provider>
  );
};

export { TokenBalancesContext, TokenBalancesProvider };